// The message templates: the first messages HQ sends to a café, written once with placeholders ({{business}}, {{city}} …) that
// are filled in for each lead. A centred dialog with the list, and an editor with clickable placeholder chips that insert at
// the cursor and a live preview against a sample café. Archiving a template stops it being offered; the messages already
// sent with it keep their credit in the funnel. With no template yet it offers a short starter to edit before saving.

import type { LeadMessageChannel, LeadTemplate } from '@clavian/api-client';
import { LEAD_TEMPLATE_PLACEHOLDERS, renderLeadTemplate } from '@clavian/domain/leads';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Dialog, EmptyState, Field, SelectField, Switch, TextField, useToast } from '@clavian/ui-web';
import { useRef, useState, type ReactNode } from 'react';

import { useProblem } from '../../api/use-problem';
import { Notice } from '../../ui';
import { LEAD_MESSAGE_CHANNEL_ORDER } from './labels';
import { useArchiveLeadTemplate, useCreateLeadTemplate, useLeadTemplates, useUpdateLeadTemplate } from './queries';

export interface TemplatesDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

/** What the editor is showing: the list, a new template (with its starting words), or one being edited. */
type Mode = { readonly kind: 'list' } | { readonly kind: 'new'; readonly name: string; readonly body: string } | { readonly kind: 'edit'; readonly template: LeadTemplate };

export function TemplatesDialog({ open, onOpenChange }: TemplatesDialogProps): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="xl" title={t('leads.templates.title')} description={t('leads.templates.description')}>
      {open ? <Templates onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function Templates({ onDone }: { readonly onDone: () => void }): ReactNode {
  const [mode, setMode] = useState<Mode>({ kind: 'list' });
  return mode.kind === 'list' ? <TemplateList onNew={(start) => setMode({ kind: 'new', name: start?.name ?? '', body: start?.body ?? '' })} onEdit={(template) => setMode({ kind: 'edit', template })} onDone={onDone} /> : <TemplateEditor mode={mode} onBack={() => setMode({ kind: 'list' })} />;
}

function TemplateList({ onNew, onEdit, onDone }: { readonly onNew: (start?: { readonly name: string; readonly body: string }) => void; readonly onEdit: (template: LeadTemplate) => void; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const [archived, setArchived] = useState(false);
  const templates = useLeadTemplates(archived);
  const archive = useArchiveLeadTemplate();

  const starter = () => onNew({ name: t('leads.templates.starterName'), body: t('leads.templates.starterBody').replace(/\[\[(\w+)\]\]/g, '{{$1}}') });

  const rows = templates.data?.data ?? [];
  let body: ReactNode;
  if (templates.data === undefined) {
    body = templates.isError ? <Notice tone="danger">{problems.text(templates.error, { fallback: t('leads.unavailable') })}</Notice> : <p className="console-subtle" aria-busy="true">…</p>;
  } else if (rows.length === 0 && !archived) {
    body = (
      <EmptyState
        title={t('leads.templates.empty')}
        description={t('leads.templates.emptyHint')}
        action={
          <div className="console-hq-actions">
            <Button variant="primary" onClick={starter}>
              {t('leads.templates.starter')}
            </Button>
            <Button variant="secondary" onClick={() => onNew()}>
              {t('leads.templates.newTemplate')}
            </Button>
          </div>
        }
      />
    );
  } else {
    body = (
      <ul className="console-hq-templates" aria-label={t('leads.templates.listLabel')}>
        {rows.map((template) => (
          <li key={template.id} className="console-hq-templates__item">
            <div className="console-hq-templates__text">
              <span className="console-hq-templates__name">
                {template.name} <Badge>{t(`leads.channel.${template.channel}`)}</Badge>
                {template.archivedAt === null ? null : <Badge tone="warn">{t('leads.templates.archivedBadge')}</Badge>}
              </span>
              <span className="console-hq-secondary console-hq-templates__preview">{template.body}</span>
            </div>
            <div className="console-hq-templates__actions">
              <Button variant="ghost" size="sm" onClick={() => onEdit(template)}>
                {t('leads.templates.edit')}
              </Button>
              {template.archivedAt === null ? (
                <Button
                  variant="ghost"
                  size="sm"
                  busy={archive.isPending && archive.variables === template.id}
                  onClick={() =>
                    archive.mutate(template.id, {
                      onSuccess: () => show({ tone: 'ok', title: t('leads.templates.archived') }),
                      onError: (error) => show({ tone: 'danger', title: problems.text(error, { fallback: t('leads.templates.archiveFailed') }) }),
                    })
                  }
                >
                  {t('leads.templates.archive')}
                </Button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="console-form">
      <div className="console-hq-templates__bar">
        <Switch label={t('leads.templates.showArchived')} checked={archived} onCheckedChange={setArchived} />
        <Button variant="secondary" onClick={() => onNew()}>
          {t('leads.templates.newTemplate')}
        </Button>
      </div>
      {body}
      <div className="console-form__actions">
        <Button variant="ghost" onClick={onDone}>
          {t('leads.templates.close')}
        </Button>
      </div>
    </div>
  );
}

function TemplateEditor({ mode, onBack }: { readonly mode: Exclude<Mode, { kind: 'list' }>; readonly onBack: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const create = useCreateLeadTemplate();
  const update = useUpdateLeadTemplate();
  const editing = mode.kind === 'edit' ? mode.template : undefined;
  const [name, setName] = useState(editing?.name ?? (mode.kind === 'new' ? mode.name : ''));
  const [channel, setChannel] = useState<LeadMessageChannel>(editing?.channel ?? 'whatsapp');
  const [body, setBody] = useState(editing?.body ?? (mode.kind === 'new' ? mode.body : ''));
  const [errors, setErrors] = useState<{ name?: string; body?: string }>({});
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const area = useRef<HTMLTextAreaElement>(null);
  const sample = {
    business: t('leads.templates.sample.business'),
    contact: t('leads.templates.sample.contact'),
    city: t('leads.templates.sample.city'),
    area: t('leads.templates.sample.area'),
    hook: t('leads.templates.sample.hook'),
    sender: t('leads.templates.sample.sender'),
  };
  const rendered = renderLeadTemplate(body, sample);
  const unknown = rendered.missing.filter((one) => !(LEAD_TEMPLATE_PLACEHOLDERS as readonly string[]).includes(one));

  /** Add a placeholder where the cursor is (or replace the selection), and put the cursor after it. */
  const insert = (placeholder: string) => {
    const token = `{{${placeholder}}}`;
    const element = area.current;
    const start = element?.selectionStart ?? body.length;
    const end = element?.selectionEnd ?? body.length;
    setBody(`${body.slice(0, start)}${token}${body.slice(end)}`);
    window.requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const save = async () => {
    const found = { ...(name.trim() === '' ? { name: t('leads.templates.nameRequired') } : {}), ...(body.trim() === '' ? { body: t('leads.templates.bodyRequired') } : {}) };
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setFailure(undefined);
    try {
      if (editing === undefined) await create.mutateAsync({ name: name.trim(), channel, body: body.trim() });
      else await update.mutateAsync({ templateId: editing.id, body: { name: name.trim(), channel, body: body.trim() } });
      show({ tone: 'ok', title: t('leads.templates.saved') });
      onBack();
    } catch (error) {
      setFailure(problems.text(error, { fallback: t('leads.templates.failed') }));
    }
  };

  return (
    <form
      className="console-form console-hq-template-editor"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      {failure === undefined ? null : (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      )}
      <div className="console-hq-template-editor__grid">
        <div className="console-form">
          <TextField label={t('leads.templates.name')} required value={name} error={errors.name} onChange={(event) => setName(event.target.value)} />
          <SelectField
            label={t('leads.templates.channel')}
            options={LEAD_MESSAGE_CHANNEL_ORDER.map((one) => ({ value: one, label: t(`leads.channel.${one}`) }))}
            value={channel}
            onValueChange={(value) => setChannel(value as LeadMessageChannel)}
          />
          <Field label={t('leads.templates.body')} description={t('leads.templates.bodyHint')} error={errors.body} required>
            {(control) => (
              <textarea
                ref={area}
                id={control.id}
                aria-describedby={control['aria-describedby']}
                aria-invalid={control['aria-invalid']}
                className="clav-input console-hq-template-editor__body"
                rows={9}
                maxLength={2000}
                value={body}
                onChange={(event) => setBody(event.target.value)}
              />
            )}
          </Field>
          <div className="console-hq-chips" role="group" aria-label={t('leads.templates.placeholders')}>
            {LEAD_TEMPLATE_PLACEHOLDERS.map((placeholder) => (
              <Button key={placeholder} type="button" variant="secondary" size="sm" onClick={() => insert(placeholder)}>
                {`{{${placeholder}}}`}
              </Button>
            ))}
          </div>
          {unknown.length === 0 ? null : (
            <Notice tone="warn" role="status">
              {t('leads.templates.unknownPlaceholders', { names: unknown.map((one) => `{{${one}}}`).join(', ') })}
            </Notice>
          )}
        </div>
        <section className="console-hq-template-editor__preview" aria-label={t('leads.templates.preview')}>
          <h3 className="console-hq-template-editor__heading">{t('leads.templates.preview')}</h3>
          <p className="console-subtle">{t('leads.templates.previewHint')}</p>
          <div className="console-hq-template-editor__bubble" data-testid="template-preview">
            {rendered.text}
          </div>
        </section>
      </div>
      <div className="console-form__actions">
        <Button type="button" variant="ghost" onClick={onBack}>
          {t('leads.templates.back')}
        </Button>
        <Button type="submit" variant="primary" busy={create.isPending || update.isPending}>
          {t(editing === undefined ? 'leads.templates.create' : 'leads.templates.save')}
        </Button>
      </div>
    </form>
  );
}
